/**
 * @file context.ts
 * @description Контекст групп настроек панели (phase19.16, SB122;
 * uiux-settings-groups §1–§2): корень `SettingsGroups` даёт группам
 * «открыта/свёрнута», собирает их для оглавления и умеет `openGroup`.
 */

import { createContext, useCallback, useContext, type MutableRefObject, type RefObject } from "react";

/** Значок состояния в заголовке группы: ✕ ошибка, ! предупреждение, замок — только просмотр. */
export type SettingsGroupStatus = "error" | "warning" | "readonly";

/** Что группа сообщает корню (для оглавления и прокрутки). */
export interface SettingsGroupMeta {
  id: string;
  title: string;
  tocLabel?: string;
  status?: SettingsGroupStatus;
  /** Карточка группы в DOM (якорь `#group-<id>`). */
  el: HTMLElement | null;
}

/** Как открыть группу. */
export interface OpenGroupOptions {
  /** Перевести фокус на заголовок (оглавление). По умолчанию нет. */
  focus?: boolean;
  /** Подсветить карточку на 1,5 с (переход по `?group=`, из схемы). По умолчанию да. */
  flash?: boolean;
  /**
   * После прокрутки — фокус на первое поле с ошибкой (`aria-invalid="true"`
   * или `data-invalid`); такого нет — на заголовок группы. По умолчанию нет.
   */
  focusInvalid?: boolean;
}

export interface SettingsGroupsApi {
  /** Ключ памяти панели. */
  panel: string;
  /** Уже 768: открыта одна группа, оглавления-пилюль нет. */
  narrow: boolean;
  /** Группы в порядке на экране. */
  groups: readonly SettingsGroupMeta[];
  isOpen: (id: string) => boolean;
  setOpen: (id: string, open: boolean) => void;
  /** Раскрыть → прокрутить к заголовку под липкую строку → подсветить/фокус. */
  openGroup: (id: string, options?: OpenGroupOptions) => void;
  /** Раскрыть все (от 768). */
  expandAll: () => void;
  register: (meta: SettingsGroupMeta) => () => void;
  /** Активная группа оглавления. */
  active: string | null;
  setActive: (id: string | null) => void;
  /** Подсвеченная после перехода группа. */
  flashId: string | null;
  /** Идёт программная прокрутка — отслеживание активной выключено. */
  scrollingRef: MutableRefObject<boolean>;
  rootRef: RefObject<HTMLDivElement | null>;
  /** Высота липкой строки сверху панели, px. */
  stickyHeight: number;
  setStickyHeight: (px: number) => void;
}

export const SettingsGroupsContext = createContext<SettingsGroupsApi | null>(null);

/** Состояние групп панели; вне `SettingsGroups` — null. */
export function useSettingsGroupsApi(): SettingsGroupsApi | null {
  return useContext(SettingsGroupsContext);
}

/** `openGroup` текущей панели; вне `SettingsGroups` — ничего не делает. */
export function useOpenGroup(): (id: string, options?: OpenGroupOptions) => void {
  const api = useContext(SettingsGroupsContext);
  const open = api?.openGroup;
  return useCallback((id: string, options?: OpenGroupOptions) => open?.(id, options), [open]);
}
